import * as React from 'react';
import { CBConstants } from './StatsbidragContactBlock.constants';
import { ITextboxItem, IForetradareDto } from './StatsbidragContactBlock.types';
import { Field } from '../StatsbidragGlobals/statsbidrag.types';
import { TextInputForPrint } from 'sos-common/js/components/FormControls/TextInputForPrint';
import { RadioButtonGroup } from 'sos-common/js/components/FormControls/RadioButtonGroup';
import { RadioButton } from 'sos-common/js/components/FormControls/RadioButton';
import _ from 'lodash';
import ValidationService from '../../services/validation.service';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import { THIS_MANDATORY } from '../CalculationComponents/calculationComponents.errorMessages';
import EventService from '../CalculationComponents/calculationComponents.eventservice';
import { stringContainsOnlyWhitelist, charCountInString } from '../../helpers/stb.helpers';
import Console from 'sos-common/js/utils/console.log';
import validateLuhn from 'sos-eservice-common/js/services/luhnvalidation';

export interface StatsbidragContactBlockProps {
  currentPerson: string;
  elementName: string;
  accountType: string;
  accountNumber: string;
  organisationContacts: IForetradareDto[];
  originalTextboxItems: ITextboxItem[];
  canChangeContactPerson: boolean;
}

export interface StatsbidragContactBlockState {
  selectedContactPersonId: string;
  selectedContactPerson: IForetradareDto;
  isValid: boolean;
  textboxItems: ITextboxItem[];
  accountType: string;
  fields: Map<string, Field>;
}

/**
 * This component is responsible of showing the selected contact person as well as the bank account information for the payout of a Statsbidrag.
 * It receives all the contact persons available for an organisation on load and the chosen person.
 * Change of the contact person from the dropdown will trigger a state change for 'selectedContactPersonId' and 'selectedContactPerson' which are bound to input fields.
 */
export class StatsbidragContactBlock extends React.Component<
  StatsbidragContactBlockProps,
  StatsbidragContactBlockState
> {
  constructor(props: StatsbidragContactBlockProps) {
    super(props);

    let accountType = props.accountType ? props.accountType : '2';
    let fields: Map<string, Field> = new Map();
    props.originalTextboxItems.forEach((txt) => {
      fields.set(txt.Name, this.getFieldModel(txt));
    });

    fields.get(CBConstants.OrgWeb).mandatory = false;
    fields.get(CBConstants.PostNr).validation = '^(\\d\\d\\d \\d\\d|\\d\\d\\d\\d\\d)$';
    fields.get(CBConstants.PostNr).maxLength = 10;

    fields.set(CBConstants.AccountType, {
      id: 'accType',
      name: CBConstants.AccountType,
      type: 'radio',
      value: props.accountType === '' ? '2' : props.accountType,
      radioHeading: 'Kontouppgifter',
      radioOptions: [
        { id: 'plusgiro', value: CBConstants.Plusgiro, label: 'Plusgiro' },
        { id: 'bankgiro', value: CBConstants.Bankgiro, label: 'Bankgiro' },
      ],
    } as Field);

    fields.set(
      CBConstants.AccountNumber,
      this.getFieldModel({
        Disabled: false,
        Id: 'accNr',
        Name: CBConstants.AccountNumber,
        LabelValue: 'Kontonummer utan mellanslag',
        Value: props.accountNumber,
      }),
    );

    this.state = {
      selectedContactPersonId: this.props.currentPerson,
      selectedContactPerson: null,
      isValid: true,
      textboxItems: this.props.originalTextboxItems,
      accountType,
      fields,
    };
  }

  componentDidMount = () => {
    this.setPerson(this.props.currentPerson);
    this.validateMe();

    EventService.subscribe('form-is-valid', (e: CustomEvent) => {
      let fields = new Map(this.state.fields);
      fields.forEach((field) => {
        field.isTouched = true;
      });
      this.setState({
        fields,
      });
    });
  };

  getFieldModel(textboxitem: ITextboxItem) {
    return {
      id: textboxitem.Id,
      type: 'text',
      label: textboxitem.LabelValue,
      name: textboxitem.Name,
      placeholder: textboxitem.Placeholder,
      mandatory: true,
      value: textboxitem.Value,
      isValid: true,
      isReadOnly: textboxitem.Disabled,
      maxLength: 160,
      isTouched: false,
      errorMessage: '',
    } as Field;
  }

  /**
   * Fields can have different validation logic, every state change triggers a validation in the _entire_ component.
   */
  validateMe = () => {
    let fields = this.state.fields;
    let componentIsValid = true;

    // Standard mandatory validation
    fields.forEach((field) => {
      if (field.mandatory && field.value === '') {
        componentIsValid = false;
        field.isValid = false;
        field.errorMessage = THIS_MANDATORY;
      } else {
        field.isValid = true;
        field.errorMessage = '';
      }
    });

    // Field-specific validation

    // Postnummer
    let postNr = fields.get(CBConstants.PostNr);
    if (postNr.isValid) {
      let isPostNrValid = new RegExp(postNr.validation).test(postNr.value);
      if (!isPostNrValid) {
        componentIsValid = false;
        postNr.isValid = isPostNrValid;
        postNr.errorMessage = `Ange uppgiften i formatet '123 45' eller '12345'`;
      }
    }

    // Kontonummer
    let accNr = fields.get(CBConstants.AccountNumber);
    let accType = fields.get(CBConstants.AccountType).value;
    let accNrVal = (accNr.value as string).trim();
    if (accNr.isValid) {
      // Check if string contains only valid characters:
      // digits, hyphens (-), spaces ( )
      let validCharacters = stringContainsOnlyWhitelist(accNrVal, [
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '-',
      ]);
      let strippedValue = accNrVal.replace(/\D/g, '');
      let noWhitespaceValue = accNrVal.replace(/\s/g, '');

      if (!validCharacters) {
        componentIsValid = false;
        accNr.isValid = false;
        accNr.errorMessage = `Använd endast siffror och bindestreck utan mellanslag`;
      } else if (accType === CBConstants.Plusgiro) {
        // 2 - 8 numbers + luhn checksum
        if (strippedValue.length < 2) {
          componentIsValid = false;
          accNr.isValid = false;
          accNr.errorMessage = `Plusgironummer kräver minst 2 siffor`;
        } else if (strippedValue.length > 8) {
          componentIsValid = false;
          accNr.isValid = false;
          accNr.errorMessage = `Plusgironummer kan högst ha 8 siffor`;
        } else if (noWhitespaceValue[noWhitespaceValue.length - 2] !== '-') {
          componentIsValid = false;
          accNr.isValid = false;
          accNr.errorMessage = `Ett bindestreck måste förekomma direkt innan sista siffran i plusgironummret`;
        } else if (charCountInString(accNrVal, '-') > 1) {
          componentIsValid = false;
          accNr.isValid = false;
          accNr.errorMessage = `Endast ett bindestreck får förekomma i plusgironummret`;
        } else {
          let isLuhnValid = validateLuhn(strippedValue);
          if (!isLuhnValid) {
            componentIsValid = false;
            accNr.isValid = false;
            accNr.errorMessage = `Det angivna plusgironummret stämmer inte`;
          }
        }
      } else if (accType === CBConstants.Bankgiro) {
        // 7 - 8 numbers + luhn checksum
        if (strippedValue.length < 7) {
          componentIsValid = false;
          accNr.isValid = false;
          accNr.errorMessage = `Bankgironummer kräver minst 7 siffor`;
        } else if (strippedValue.length > 8) {
          componentIsValid = false;
          accNr.isValid = false;
          accNr.errorMessage = `Bankgironummer kan högst ha 8 siffor`;
        } else if (noWhitespaceValue[noWhitespaceValue.length - 5] !== '-') {
          componentIsValid = false;
          accNr.isValid = false;
          accNr.errorMessage = `Ett bindestreck måste förekomma direkt innan de fyra sista siffrorna i bankgironummret`;
        } else if (charCountInString(accNrVal, '-') > 1) {
          componentIsValid = false;
          accNr.isValid = false;
          accNr.errorMessage = `Endast ett bindestreck får förekomma i bankgironummret`;
        } else {
          let isLuhnValid = validateLuhn(strippedValue);
          if (!isLuhnValid) {
            componentIsValid = false;
            accNr.isValid = false;
            accNr.errorMessage = `Det angivna bankgironummret stämmer inte`;
          }
        }
      }
    }

    this.setState(
      {
        isValid: componentIsValid,
        fields,
      },
      () => {
        ValidationService.validateStepViaElementName(this.props.elementName);
      },
    );
  };

  setPerson = (personId: string) => {
    let newPerson = this.props.organisationContacts.find((x) => x.Id === personId);
    if (!newPerson) {
      Console.warn('Unable to find contact person');
      return;
    }

    // clone fields
    let fields = new Map(this.state.fields);
    let updateData = [
      {
        name: CBConstants.OrgEpost,
        value: newPerson.Organisation.Epost,
      },
      {
        name: CBConstants.ContactEpost,
        value: newPerson.Epost,
      },
      {
        name: CBConstants.ContactTel,
        value: newPerson.Telefon,
      },
      {
        name: CBConstants.ContactTitle,
        value: newPerson.Befattning,
      },
      {
        name: CBConstants.OrgWeb,
        value: newPerson.Organisation.Webbplats,
      },
    ];

    updateData.forEach((data) => {
      fields.get(data.name).value = data.value;
    });

    this.setState(
      {
        selectedContactPersonId: personId,
        selectedContactPerson: newPerson,
        fields,
      },
      () => {
        this.validateMe();
      },
    );
  };

  contactPersonChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
    let selectedPersonId = event.target.value;
    this.setPerson(selectedPersonId);
  };

  renderDropdownOptions = (el: IForetradareDto, index: number) => {
    return (
      <option key={el.Id} value={el.Id} disabled={!this.props.canChangeContactPerson}>
        {el.Person.Fornamn + ' ' + el.Person.Efternamn}
      </option>
    );
  };

  fieldChanged = (formfield: FormField<any>) => {
    let fields = new Map(this.state.fields);
    let field = fields.get(formfield.fieldName);
    field.value = formfield.value;
    field.isTouched = true;

    // if we change Account Type, we also wanna 'touch' Account Number
    if (field.name === CBConstants.AccountType) {
      let accNrField = fields.get(CBConstants.AccountNumber);
      accNrField.isTouched = true;
    }

    this.setState(
      {
        fields,
      },
      () => {
        this.validateMe();
      },
    );
  };

  renderField = (field: Field) => {
    let extraProps = {};
    if (!field.isReadOnly) {
      extraProps['isRequired'] = true;
    }
    return (
      <div className="col-md-6" key={field.id}>
        <div className="form-group-small">
          {field.type === 'text' && (
            <React.Fragment>
              <label htmlFor={field.id}>{field.label}</label>
              <TextInputForPrint
                isControlledInput={field.isReadOnly}
                id={field.id}
                fieldName={field.name}
                disabled={field.isReadOnly}
                type={field.type}
                value={field.value}
                maxLength={field.maxLength}
                onChange={field.isReadOnly ? undefined : this.fieldChanged}
                errorMessage={!field.isValid && field.isTouched ? field.errorMessage : ''}
                {...extraProps}
              />
            </React.Fragment>
          )}
          {field.type === 'radio' && (
            <fieldset>
              <legend className="like-a-label contact-radio">{field.radioHeading}</legend>
              <RadioButtonGroup onChange={this.fieldChanged} value={field.value} fieldName={field.name}>
                {field.radioOptions &&
                  field.radioOptions.map((radioOp) => {
                    return <RadioButton key={radioOp.id} id={radioOp.id} value={radioOp.value} label={radioOp.label} />;
                  })}
              </RadioButtonGroup>
            </fieldset>
          )}
        </div>
      </div>
    );
  };

  renderRow = (field: [string, Field], index: number, fieldArr: [string, Field][]) => {
    if (index % 2 == 1) return;
    let next = fieldArr.length - 1 >= index ? fieldArr[index + 1] : null;
    return (
      <div className="row" key={`row-${index}`}>
        {this.renderField(field[1])}
        {next && this.renderField(next[1])}
      </div>
    );
  };

  // dictionaryToArray = (dict: any) => {
  //   let arr = [];
  //   for (const [key, value] of Object.entries(dict)) {
  //     arr.push(value);
  //   }
  //   return arr;
  // };

  public render() {
    const { isValid, selectedContactPersonId, fields } = this.state;
    const { elementName, organisationContacts } = this.props;
    return (
      <div className="stb-contactdetails">
        <input type="hidden" name={elementName} data-isvalid={isValid} />
        <input type="hidden" name={CBConstants.SelectedContactPerson} defaultValue={selectedContactPersonId} />

        {organisationContacts && (
          <div className="row">
            <div className="col-md-6">
              <div className="form-group-small">
                <div>
                  <label htmlFor="selectedContact">Kontaktperson</label>
                </div>
                <select
                  id="selectedContact"
                  onChange={this.contactPersonChanged}
                  defaultValue={selectedContactPersonId}>
                  {organisationContacts.map(this.renderDropdownOptions)}
                </select>
              </div>
            </div>
          </div>
        )}
        {[...fields].map(this.renderRow)}
      </div>
    );
  }
}
